import { useCallback } from "react";
import { PointerActivationConstraints } from "@dnd-kit/dom";
import { DragDropProvider, PointerSensor, useDraggable, useDroppable } from "@dnd-kit/react";
import styled from "@emotion/styled";
import { Dice5 } from "lucide-react";

import { generateRandomTeams } from "@/features/match/matchFlow.machine";
import type { useMatchFlow } from "@/features/match/useMatchFlow";
import { useTranslations } from "@/i18n";
import { usePlayers } from "@/store/selectors";
import { isTeams, type Player } from "@/types";
import type { PlayerId } from "@/types/branded";
import { TEAM_COLORS, type TeamColorKey, theme } from "@/ui/theme";

// ── DnD config ───────────────────────────────────────────────

const PLAYER_DND_TYPE = "PLAYER";

const TEAM_COLOR_ORDER: TeamColorKey[] = ["team-a", "team-b", "team-c", "team-d"];

const pointerSensor = PointerSensor.configure({
  activationConstraints(event) {
    switch (event.pointerType) {
      case "mouse":
        return [new PointerActivationConstraints.Distance({ value: 5 })];
      case "touch":
        return [new PointerActivationConstraints.Delay({ value: 0, tolerance: 5 })];
      default:
        return [
          new PointerActivationConstraints.Delay({
            value: 200,
            tolerance: 10,
          }),
          new PointerActivationConstraints.Distance({ value: 5 }),
        ];
    }
  },
});

// ── Styled ───────────────────────────────────────────────────

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const StageTag = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: ${theme.colors.textMuted};
`;

const Title = styled.h2`
  font-family: ${theme.fonts.display};
  font-size: 1.875rem;
  line-height: 1.1;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
`;

const ActionsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PillBtn = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: ${theme.radii.full};
  background: ${theme.colors.secondary};
  font-size: 0.875rem;
  font-weight: 500;
  &:hover {
    background: ${theme.colors.secondaryHover};
  }
`;

const Court = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${theme.radii["3xl"]};
  border: 1px solid ${theme.colors.border};
  background: ${theme.gradients.court};
  padding: 0.75rem;
  box-shadow: ${theme.shadow.card};

  background-image:
    linear-gradient(${theme.colors.courtLine} 1px, transparent 1px),
    linear-gradient(90deg, ${theme.colors.courtLine} 1px, transparent 1px);
  background-size: 32px 32px;
`;

const CourtTeams = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
`;

const TeamSlot = styled.div<{ teamColor: string; isOver: boolean }>`
  min-height: 6rem;
  border-radius: ${theme.radii["2xl"]};
  padding: 0.75rem;
  text-align: left;
  background: linear-gradient(
    135deg,
    color-mix(in oklab, ${(p) => p.teamColor} 20%, transparent),
    transparent
  );
  border: ${(p) =>
    p.isOver
      ? `1px solid color-mix(in oklab, ${theme.colors.primary} 70%, transparent)`
      : `1px solid color-mix(in oklab, ${p.teamColor} 40%, transparent)`};
  transition: all 0.15s;
`;

const TeamSlotHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const TeamName = styled.h3<{ color: string }>`
  font-family: ${theme.fonts.display};
  font-size: 1.125rem;
  color: ${(p) => p.color};
`;

const PlayerCount = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const ChipList = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const EmptyHint = styled.li`
  font-size: 0.75rem;
  font-style: italic;
  color: color-mix(in oklab, ${theme.colors.textMuted} 70%, transparent);
`;

const BenchSlot = styled.div<{ isOver: boolean }>`
  width: 100%;
  border-radius: ${theme.radii["2xl"]};
  border: 1px dashed
    ${(p) =>
      p.isOver
        ? `color-mix(in oklab, ${theme.colors.primary} 60%, transparent)`
        : theme.colors.border};
  background: ${(p) =>
    p.isOver
      ? `color-mix(in oklab, ${theme.colors.primary} 5%, transparent)`
      : `color-mix(in oklab, ${theme.colors.bgCard} 40%, transparent)`};
  padding: 0.75rem;
  text-align: left;
  transition: all 0.15s;
`;

const BenchHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const BenchLabel = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${theme.colors.textMuted};
`;

const ChipBtn = styled.span<{ isDragging?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: ${theme.radii.full};
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bg} 80%, transparent);
  color: ${theme.colors.text};
  cursor: grab;
  touch-action: none;
  opacity: ${(p) => (p.isDragging ? 0.4 : 1)};
  transition: opacity 0.15s;
`;

// ── DnD sub-components ───────────────────────────────────────

const DraggableChip = ({ player }: { player: Player }) => {
  const { ref, isDragging } = useDraggable({
    id: `player-${player.id}`,
    type: PLAYER_DND_TYPE,
    data: { playerId: player.id },
  });

  return (
    <li>
      <ChipBtn ref={ref} isDragging={isDragging}>
        {player.name}
      </ChipBtn>
    </li>
  );
};

const DroppableTeam = ({
  teamIndex,
  team,
  teamColor,
  playersById,
}: {
  teamIndex: number;
  team: PlayerId[];
  teamColor: string;
  playersById: Record<PlayerId, Player>;
}) => {
  const { t } = useTranslations();
  const { ref, isDropTarget } = useDroppable({
    id: `team-${teamIndex}`,
    accept: PLAYER_DND_TYPE,
    data: { teamIndex },
  });

  const teamPlayers = team.map((pid) => playersById[pid]).filter(Boolean) as Player[];

  return (
    <TeamSlot ref={ref} teamColor={teamColor} isOver={isDropTarget}>
      <TeamSlotHeader>
        <TeamName color={teamColor}>{t("general.teamN", teamIndex + 1)}</TeamName>
        <PlayerCount>{t("general.players", teamPlayers.length)}</PlayerCount>
      </TeamSlotHeader>
      <ChipList>
        {teamPlayers.map((p) => (
          <DraggableChip key={p.id} player={p} />
        ))}
        {teamPlayers.length === 0 && (
          <EmptyHint>{isDropTarget ? t("arena.dropHere") : t("arena.dragHere")}</EmptyHint>
        )}
      </ChipList>
    </TeamSlot>
  );
};

const DroppableBench = ({ unassigned }: { unassigned: Player[] }) => {
  const { t } = useTranslations();
  const { ref, isDropTarget } = useDroppable({
    id: "bench",
    accept: PLAYER_DND_TYPE,
    data: { teamIndex: -1 },
  });

  return (
    <BenchSlot ref={ref} isOver={isDropTarget}>
      <BenchHeader>
        <BenchLabel>{t("arena.bench")}</BenchLabel>
        <PlayerCount>{unassigned.length}</PlayerCount>
      </BenchHeader>
      <ChipList>
        {unassigned.map((p) => (
          <DraggableChip key={p.id} player={p} />
        ))}
        {unassigned.length === 0 && <EmptyHint>{t("arena.everyonePlaying")}</EmptyHint>}
      </ChipList>
    </BenchSlot>
  );
};

// ── Component ────────────────────────────────────────────────

type FlowApi = ReturnType<typeof useMatchFlow>;

export const LineupStage = ({ flow }: { flow: FlowApi }) => {
  const { t } = useTranslations();
  const playersById = usePlayers();
  const { activity, teams, unassigned, setTeams } = flow;

  const addToTeam = useCallback(
    (playerId: PlayerId, teamIndex: number) => {
      if (teamIndex === -1) {
        // Move to bench = remove from all teams
        setTeams(teams.map((t) => t.filter((pid) => pid !== playerId)));
        return;
      }
      const without = teams.map((t) => t.filter((pid) => pid !== playerId));
      without[teamIndex] = [...(without[teamIndex] ?? []), playerId];
      setTeams(without);
    },
    [teams, setTeams],
  );

  const handleRandomize = useCallback(() => {
    if (!activity || !isTeams(activity)) return;
    setTeams(generateRandomTeams(activity.numTeams, activity.teamSizes, flow.participantIds));
  }, [activity, flow.participantIds, setTeams]);

  if (!activity) return null;

  return (
    <DragDropProvider
      sensors={(sensors) => [...sensors, pointerSensor]}
      onDragEnd={(event) => {
        if (event.canceled) return;
        const { source, target } = event.operation;
        if (source && target) {
          const playerId = source.data?.playerId as PlayerId | undefined;
          const teamIndex = target.data?.teamIndex as number | undefined;
          if (playerId != null && teamIndex != null) {
            addToTeam(playerId, teamIndex);
          }
        }
      }}
    >
      <Content>
        <header>
          <StageTag>{t("arena.stageLineup")}</StageTag>
          <Title>{activity.name}</Title>
          <Subtitle>{t("arena.lineupSubtitle")}</Subtitle>
        </header>

        <ActionsBar>
          <PillBtn onClick={handleRandomize}>
            <Dice5 size={16} /> {t("arena.shuffle")}
          </PillBtn>
        </ActionsBar>

        <Court>
          <CourtTeams>
            {teams.map((team, i) => {
              const colorKey = TEAM_COLOR_ORDER[i % TEAM_COLOR_ORDER.length]!;
              return (
                <DroppableTeam
                  key={i}
                  teamIndex={i}
                  team={team}
                  teamColor={TEAM_COLORS[colorKey]}
                  playersById={playersById}
                />
              );
            })}
          </CourtTeams>
        </Court>

        <DroppableBench unassigned={unassigned} />
      </Content>
    </DragDropProvider>
  );
};
